import React, {
  ReactElement,
  useState,
  useEffect,
  useContext,
  useRef,
  MutableRefObject,
} from "react";
import { useLocation } from "react-router";
import { LoginContext } from "../../../context/categoryContext";
import { ApiService } from "../../../services/api.service";
import AdminHeader from "../shared/header";
import AdminNav from "../shared/nav";
import { useForm } from "react-hook-form";
import { SubCatContext } from "../../../context/subCategoryContext";
import Loader from "../../Loader";
import { Link, useSearchParams } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import { ProductContext } from "../../../context/prodContext";
import { use } from "i18next";

interface RelatedProduct {
  relatedID: number;
  productID: number;
  relatedProductID: number;
  productName: string;
  productDetails?: string;
  productStatus?: number;
}

const RelatedProducts: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [relatedData, setRelatedData] = useState<RelatedProduct[]>([]);
  const [hasRelated, setHasRelated] = useState<boolean>(false);
  const [productId, setProductId] = useState<number | null>(null);

  const [allProducts, setAllProducts] = useState<any | null>(null);

  const [selectedNewProductId, setSelectedNewProductId] = useState<
    number | null
  >(null);

  const apiService = new ApiService();

  const fetchRelatedData = async (id: number) => {
    try {
      const response = await apiService.getRelatedProducts(id);
      setRelatedData(response);
      setHasRelated(response.length > 0);
    } catch (error) {
      console.error("Error fetching related products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRelated = async () => {
    if (!selectedNewProductId || !productId) return;

    const postPayload = {
      productID: productId,
      relatedProductID: selectedNewProductId,
    };

    try {
      console.log("Posting related product:", postPayload);
      const response = await apiService.PostData(
        "/api/Related/SaveRelated", // 🔁 Adjust if needed
        postPayload
      );

      console.log("Add response:", response);

      // Refresh the related products list
      fetchRelatedData(productId);
      setSelectedNewProductId(null); // reset form
    } catch (error) {
      console.error("Failed to add related product:", error);
    }
  };

  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        const response = await apiService.getProducts();

        const sortedByProductName = response
          .filter((p: any) => p.productName !== "")
          .sort((a: any, b: any) => a.productName.localeCompare(b.productName));

        setAllProducts(sortedByProductName);
      } catch (error) {
        console.error("Error fetching all products:", error);
      }
    };

    fetchAllProducts();
  }, []);

  const handleDelete = async (relatedID: number) => {
    if (
      !window.confirm("Are you sure you want to delete this related product?")
    )
      return;

    try {
      const response = await apiService.PostData(
        "/api/Related/DeleteRelated",
        relatedID
      );

      if (response === 1 && productId) {
        fetchRelatedData(productId); // Refresh table
      } else {
        alert("Failed to delete related product.");
      }
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  useEffect(() => {
    const idParam = searchParams.get("id");

    if (idParam) {
      const parsedId = parseInt(idParam, 10);
      if (!isNaN(parsedId)) {
        setProductId(parsedId);
        fetchRelatedData(parsedId);
      } else {
        console.warn("Invalid product ID");
        setLoading(false);
      }
    } else {
      console.warn("No product ID provided in URL");
      setLoading(false);
    }
  }, [searchParams]);

  return (
    <div>
      <AdminHeader />
      <div className="container-fluid">
        <div className="row">
          <AdminNav />
          <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4">
            {loading ? (
              <Loader />
            ) : (
              <>
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom">
                  <h1 className="h2">
                    Related Products for: <b>{productId}</b>
                  </h1>
                </div>

                {hasRelated ? (
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead className="table-light">
                        <tr>
                          <th scope="col">Related ID</th>
                          <th scope="col">Product ID</th>
                          <th scope="col">Related Product ID</th>
                          <th scope="col">Name</th>
                          <th scope="col">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {relatedData.map((item) => (
                          <tr key={item.relatedID}>
                            <td>{item.relatedID}</td>
                            <td>{item.productID}</td>
                            <td>{item.relatedProductID}</td>
                            <td>{item.productName}</td>
                            <td>
                              {item.productStatus === 1 ? "Active" : "Inactive"}
                            </td>
                            <td>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDelete(item.relatedID)}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p>No related products found.</p>
                )}
              </>
            )}
            <Form.Group controlId="addRelatedProduct">
              <Form.Label>Add a Related Product</Form.Label>
              <p
                style={{
                  color: "gray",
                  fontSize: 14,
                }}
              >
                Note: This may take a while to load
              </p>
              <Form.Select
                value={selectedNewProductId ?? ""}
                onChange={(e) =>
                  setSelectedNewProductId(Number(e.target.value))
                }
              >
                <option value="">Select a product</option>
                {allProducts
                  ?.filter((p: any) => p.productID !== productId) // exclude current
                  .map((p: any) => (
                    <option key={p.productID} value={p.productID}>
                      {p.productName}
                    </option>
                  ))}
              </Form.Select>
              <Button
                className="mt-2"
                variant="primary"
                onClick={handleAddRelated}
                disabled={!selectedNewProductId}
              >
                Add Related Product
              </Button>
            </Form.Group>
          </main>
        </div>
      </div>
    </div>
  );
};

export default RelatedProducts;
